import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { graphqlApi } from '~/shared/store/api-services/graphql-api';
import { categoriesSlice } from '~/shared/store/slices/categories/categories-slice';
import { clientWorkspaceSlice } from '~/shared/store/slices/client-workspace/client-workspace-slice';
import { companiesSlice } from '~/shared/store/slices/companies/companies-slice';
import { consultationsSlice } from '~/shared/store/slices/consultations/consultations-slice';
import { courseCategoriesSlice } from '~/shared/store/slices/course-categories/course-categories-slice';
import { coursesSlice } from '~/shared/store/slices/courses/courses-slice';
import { dialogsSlice } from '~/shared/store/slices/dialogs/dialogs-slice';
import { filesSlice } from '~/shared/store/slices/files/files-slice';
import { formTemplatesSlice } from '~/shared/store/slices/form-templates/form-templates-slice';
import { homeTasksSlice } from '~/shared/store/slices/home-tasks/home-tasks-slice';
import { lessonExercisesSlice } from '~/shared/store/slices/lesson-exercises/lesson-exercises-slice';
import { lessonTemplatesSlice } from '~/shared/store/slices/lesson-templates/lesson-templates-slice';
import { lessonsSlice } from '~/shared/store/slices/lessons/lessons-slice';
import { messagesSlice } from '~/shared/store/slices/messages/messages-slice';
import { modalSlice } from '~/shared/store/slices/modal/modal-slice';
import { postsSlice } from '~/shared/store/slices/posts/posts-slice';
import { tagsSlice } from '~/shared/store/slices/tags/tags-slice';
import { userGroupsSlice } from '~/shared/store/slices/user-groups/user-groups-slice';
import { usersSlice } from '~/shared/store/slices/users/users-slice';
import { votesSlice } from '~/shared/store/slices/votes/votes-slice';

import { appSlice } from './slices/app/app-slice';
import { calendarSlice } from './slices/calendar/calendar-slice';

const isRtkq = (type: string) => type?.startsWith(`${graphqlApi.reducerPath}/`); // у тебя reducerPath = 'graphql'

// Sanitize actions for DevTools (cut RTKQ payload)
const actionSanitizer = (action: any) => {
  const a = { ...action };

  // already done: cleaning up the heavy baseQueryMeta
  if (a?.meta?.baseQueryMeta) {
    a.meta = {
      ...a.meta,
      baseQueryMeta: {
        ...a.meta.baseQueryMeta,
        request: a.meta.baseQueryMeta.request ? '[Request omitted]' : undefined,
        response: a.meta.baseQueryMeta.response ? '[Response omitted]' : undefined,
      },
    };
  }

  // Let's completely cut off the payload of RTK Query actions
  if (isRtkq(a.type)) {
    return { ...a, payload: '[RTK Query payload omitted]' };
  }

  // your case with Stream
  if (a?.payload?.stream) {
    a.payload = { ...a.payload, stream: '[MediaStream omitted]' };
  }
  return a;
};

const summarizeMap = (obj?: Record<string, any>) => {
  const out: Record<string, any> = {};
  if (!obj) return out;
  for (const k of Object.keys(obj)) out[k] = { __omitted__: true };
  return out;
};

const stateSanitizer = (state: any) => {
  const s = { ...state };
  const api = s[graphqlApi.reducerPath];

  if (api && typeof api === 'object') {
    s[graphqlApi.reducerPath] = {
      ...api,
      // leave the object, but clean up the details
      queries: summarizeMap(api.queries),
      mutations: summarizeMap(api.mutations),
      subscriptions: summarizeMap(api.subscriptions),
      provided: Object.fromEntries(
        Object.entries(api.provided ?? {}).map(([type, val]) => [type, summarizeMap(val as Record<string, any>)]),
      ),
    };
  }
  // your other clippings (MediaStream, etc.) - without changing types
  if (s.call?.stream) s.call = { ...s.call, stream: '[MediaStream omitted]' };

  return s;
};

const store = configureStore({
  reducer: {
    app: appSlice.reducer,
    calendar: calendarSlice.reducer,
    dialogs: dialogsSlice.reducer,
    courses: coursesSlice.reducer,
    courseCategories: courseCategoriesSlice.reducer,
    categories: categoriesSlice.reducer,
    lessonTemplates: lessonTemplatesSlice.reducer,
    modal: modalSlice.reducer,
    tags: tagsSlice.reducer,
    users: usersSlice.reducer,
    clientWorkspace: clientWorkspaceSlice.reducer,
    lessons: lessonsSlice.reducer,
    lessonExercises: lessonExercisesSlice.reducer,
    files: filesSlice.reducer,
    votes: votesSlice.reducer,
    formTemplates: formTemplatesSlice.reducer,
    homeTasks: homeTasksSlice.reducer,
    messages: messagesSlice.reducer,
    posts: postsSlice.reducer,
    companies: companiesSlice.reducer,
    userGroups: userGroupsSlice.reducer,
    consultations: consultationsSlice.reducer,
    [graphqlApi.reducerPath]: graphqlApi.reducer,
  },
  devTools:
    process.env.NODE_ENV !== 'production'
      ? {
          trace: true,
          traceLimit: 100,
        }
      : false,
  /*  devTools:
    process.env.NODE_ENV !== 'production'
      ? {
          trace: true,
          traceLimit: 25,
          actionSanitizer,
          stateSanitizer,
        }
      : false,*/
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(graphqlApi.middleware),
});

setupListeners(store.dispatch);

export default store;

export const persistor = store;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
